import React from 'react';

import { Lock } from 'lucide-react';

import { cn } from '../lib/utils';
import { chipColors } from '../utils/labelChipColors';

type LabelChipProps = {
  /** The owning category's hex colour. Tinted into a bg/fg pair by `chipColors`. */
  categoryColor: string;
  children: React.ReactNode;
  className?: string;
  /** A person set this label by hand: a lock is drawn before the name. */
  locked?: boolean;
  /** Names the lock for screen readers; without it the lock is decorative. */
  lockLabel?: string;
};

/**
 * The label tag chip. The tinted pair is handed to Tailwind through CSS custom
 * properties so the class list stays static (no runtime-built class names, which
 * the JIT compiler cannot see).
 *
 * It lives here rather than beside the Settings screen it was written for
 * because five surfaces draw it now — Settings, the Performance data table, the
 * Optimize proposal, the Content tab and the Filters drawer. Its colour maths
 * moved with it, to `src/utils/labelChipColors.ts`: this folder may not import
 * from `routes/`, and the old home also carried the label RULE vocabulary.
 *
 * Box geometry comes from the Figma `Calculation Element` master, read off the
 * vector data of two instances rather than a frame export:
 *
 *   rules table    12135:41911 -> 32 x 26, text box at x=10, y=4, h=18
 *   contents table 12135:44439 -> 36 x 26, text box at x=10, y=4, h=18
 *
 * Both instances carry the same master child (`4410:12`), so there is exactly
 * one chip size in the design — 10px horizontal padding, 4px vertical padding,
 * an 18px line box, 26px tall. The widths differ only because the label text
 * does. No size variant exists to model, so none is offered here.
 *
 * FIGMA-SPEC's prose asks for radius 4, padding `3px 10px` and .26px tracking.
 * That is the spec's WORDS against this master's MEASUREMENTS, and a 1px delta
 * is not worth churning five shipped screens for; the measurements stay.
 */
export function LabelChip({
  categoryColor,
  children,
  className,
  locked = false,
  lockLabel,
}: LabelChipProps): JSX.Element {
  const { bg, fg } = chipColors(categoryColor);

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-[3px] px-2.5 py-1 text-[13px] font-semibold leading-[18px]',
        // The chip's own ceiling, and the reason a `truncate` INSIDE it works.
        // Without it the chip is an inline-flex with no bound: a 100-character
        // name laid out at 711px inside a 220px column, `max-w-full` on the span
        // within resolved against that 711px and never fired, and the name was
        // cut mid-word by the column's `overflow-hidden` with no ellipsis to say
        // so. `min-w-0` is the other half — a flex item will not shrink below its
        // content without it, so the ceiling alone would just overflow instead.
        'min-w-0 max-w-full',
        'bg-[var(--cbg)] text-[var(--cfg)]',
        locked && 'gap-1',
        className,
      )}
      style={{ '--cbg': bg, '--cfg': fg } as React.CSSProperties}
    >
      {locked ? (
        lockLabel ? (
          <Lock role="img" aria-label={lockLabel} className="size-3 shrink-0" />
        ) : (
          <Lock aria-hidden="true" className="size-3 shrink-0" />
        )
      ) : null}
      {children}
    </span>
  );
}
